import { createToaster } from "@chakra-ui/react";
import { checkForReactNativeAttribute } from "@shared/hooks/platform/react-native";
import type { ToastSharedProps } from "./Toast.props";

// Chakra v3 toaster (requires a Toaster renderer mounted in the app)

const defaultToasterProps = { pauseOnPageIdle: true };

export const toaster = createToaster({
  ...defaultToasterProps,
  placement: "top-end",
});

export const webviewToaster = createToaster({
  ...defaultToasterProps,
  placement: "top-center",
});

/**
 * Returns the appropriate toaster instance based on the current environment.
 * - Returns `webviewToaster` (top-center placement) when running in a React Native WebView
 * - Returns `toaster` (top-end placement) for regular web browsers
 */
export const getActiveToaster = () => {
  return checkForReactNativeAttribute() ? webviewToaster : toaster;
};

const config = {
  duration: 9000,
  closable: true,
};

export default function Toast(props: ToastProps): string | number | undefined {
  const { status, title, description, id } = props;
  const activeToaster = getActiveToaster();

  let returnToast: ReturnType<typeof toaster.create> | undefined;

  const create = (toastId?: string) =>
    activeToaster.create({
      ...config,
      ...(toastId ? { id: toastId } : {}),
      type: status,
      title,
      description,
      // If you need custom rendering, mount a Chakra `<Toaster toaster={toaster}>`
      // and render `<ToastAlert />` from its render function.
    });

  if (id) {
    if (!activeToaster.isVisible(id)) {
      returnToast = create(id);
    }
  } else {
    returnToast = create();
  }

  return returnToast;
}

export type ToastProps = ToastSharedProps;
